import { clsx } from 'clsx'
import { Bug, ChevronLeft, ChevronRight, Gift } from 'lucide-react'
import { Fragment, useState } from 'react'
import { BugReportDetailPanel } from '@/components/bug-reports/bug-report-detail-panel'
import { LoadingPage } from '@/components/ui/loading'
import { StatusBadge } from '@/components/ui/status-badge'
import type { BugReport } from '@/hooks/use-bug-reports'
import { useAdminBugReports } from '@/hooks/use-bug-reports'

const PER_PAGE = 20

const STATUS_OPTIONS = [
  'all',
  'new',
  'reviewing',
  'accepted',
  'duplicate',
  'rejected',
  'fixed',
] as const
const CATEGORY_OPTIONS = ['all', 'security', 'ui', 'api', 'other'] as const

export function BugReportsPage(): React.JSX.Element {
  const [statusFilter, setStatusFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data, isLoading, error } = useAdminBugReports({
    page,
    per_page: PER_PAGE,
    status: statusFilter === 'all' ? undefined : statusFilter,
    category: categoryFilter === 'all' ? undefined : categoryFilter,
  })

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <div className="rounded-xl border border-danger/30 bg-surface-0 p-12 text-center">
          <p className="text-danger">Failed to load bug reports. Please try again.</p>
        </div>
      </div>
    )
  }

  const reports = data?.data ?? []
  const totalPages = data ? Math.ceil(data.total / data.per_page) : 1
  const showStart = data ? (data.page - 1) * data.per_page + 1 : 0
  const showEnd = data ? Math.min(data.page * data.per_page, data.total) : 0

  return (
    <div className="space-y-6">
      <PageHeader />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
          className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary focus:border-admin-500 focus:outline-none"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s === 'all' ? 'All Statuses' : s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value)
            setPage(1)
          }}
          className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary focus:border-admin-500 focus:outline-none"
        >
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c === 'all' ? 'All Categories' : c.charAt(0).toUpperCase() + c.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <LoadingPage message="Loading bug reports..." />
      ) : reports.length === 0 ? (
        <div className="flex items-center justify-center rounded-xl border border-surface-3 bg-surface-0 py-16">
          <p className="text-text-secondary">No bug reports found matching your filters.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-surface-3 bg-surface-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-surface-3 bg-surface-1">
                <tr>
                  {['ID', 'Title', 'Reporter', 'Category', 'Severity', 'Status', 'Date'].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-secondary"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-2">
                {reports.map((report) => (
                  <Fragment key={report.id}>
                    <ReportRow
                      report={report}
                      isExpanded={expandedId === report.id}
                      onToggle={() => setExpandedId(expandedId === report.id ? null : report.id)}
                    />
                    {expandedId === report.id && (
                      <BugReportDetailPanel report={report} onClose={() => setExpandedId(null)} />
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data && (
            <div className="flex items-center justify-between border-t border-surface-3 px-4 py-3">
              <p className="text-sm text-text-secondary">
                Showing {showStart}-{showEnd} of {data.total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-lg p-1.5 text-text-secondary hover:bg-surface-2 disabled:opacity-40"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-text-secondary">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  className="rounded-lg p-1.5 text-text-secondary hover:bg-surface-2 disabled:opacity-40"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function PageHeader(): React.JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <Bug className="h-6 w-6 text-admin-500" />
      <h1 className="text-2xl font-semibold text-text-primary">Bug Reports</h1>
    </div>
  )
}

function ReportRow({
  report,
  isExpanded,
  onToggle,
}: {
  report: BugReport
  isExpanded: boolean
  onToggle: () => void
}): React.JSX.Element {
  return (
    <tr
      className={clsx(
        'cursor-pointer transition-colors hover:bg-surface-1',
        isExpanded && 'bg-surface-1',
      )}
      onClick={onToggle}
    >
      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-text-secondary">
        {report.id.slice(0, 12)}
      </td>
      <td className="whitespace-nowrap px-4 py-3 font-medium text-text-primary">{report.title}</td>
      <td className="whitespace-nowrap px-4 py-3 text-text-primary">{report.reporter_email}</td>
      <td className="whitespace-nowrap px-4 py-3">
        <StatusBadge status={report.category} />
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        <StatusBadge status={report.severity} />
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        <span className="inline-flex items-center gap-1.5">
          <StatusBadge status={report.status} />
          {report.rewarded && <Gift className="h-3.5 w-3.5 text-amber-500" />}
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
        {new Date(report.created_at).toLocaleDateString()}
      </td>
    </tr>
  )
}
